import { Stack, Tooltip, Typography, useTheme } from '@ethings/ui-toolkit';
import { Box, Card, Link, styled, Theme, useMediaQuery } from '@mui/material';
import chroma from 'chroma-js';
import { FC, useState } from 'react';
import { useResponsiveText } from '../../hooks';
import { innerText } from '../../util';
import { DataCardProps } from './data-card.types';

interface ContainerProps {
  backgroundColor?: string;
  wrap?: boolean;
}

const Container = styled(Card)<ContainerProps>(({ backgroundColor, theme, wrap }: ContainerProps & { theme: Theme }) => ({
  position: 'relative',
  alignItems: 'center',
  borderRadius: theme.dataCard.borderRadius,
  background: backgroundColor || theme.dataCard.background,
  color: theme.dataCard.contrastText,
  width: '100%',
  padding: `${theme.spacing(2)} ${theme.spacing(1.5)}`,
  '& .avatar': {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': {
      fontSize: theme.dataCard.avatarSize,
      maxWidth: '1em',
      maxHeight: '1em',
    },
    '& svg': {
      fontSize: '2rem',
    },
  },
  '& .indicator': {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  '& .content': {
    minWidth: 0,
    width: '100%',
  },
  '& .label, .title, .hint': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: wrap ? 'normal' : 'nowrap',
  },
  '& a': {
    textDecoration: 'none',
  },
  '& .label, .hint': {
    color: chroma(theme.dataCard.contrastText).alpha(0.7).hex(),
  },
}));

export const DataCard: FC<DataCardProps> = ({
  avatar,
  backgroundColor,
  hint,
  href,
  indicator,
  label,
  title,
  tooltip,
  wrap,
  ...props
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const avatarSize = isSmallScreen ? '50px' : '60px';

  const labelLength = innerText(label).length;
  const titleLength = innerText(title).length;
  const hintLength = innerText(hint).length;

  const labelStyles = useResponsiveText('0.75rem', labelLength, {
    minFontSize: '0.65rem',
    maxFontSize: '0.75rem',
    textLengthThreshold: 20,
  });

  const titleStyles = useResponsiveText('1rem', titleLength, {
    minFontSize: '0.8rem',
    maxFontSize: '1rem',
    textLengthThreshold: 25,
  });

  const hintStyles = useResponsiveText('0.75rem', hintLength, {
    minFontSize: '0.65rem',
    maxFontSize: '0.75rem',
    textLengthThreshold: 30,
  });

  return <Tooltip
    arrow
    onMouseEnter={() => setTooltipOpen(true)}
    onMouseLeave={() => setTooltipOpen(false)}
    open={tooltipOpen}
    title={tooltip}
  >
    <Container
      backgroundColor={backgroundColor}
      data-testid={props['data-testid']}
      elevation={tooltipOpen && tooltip ? 1 : 0}
      wrap={wrap}
    >
      {indicator ? <Box className='indicator'>{indicator}</Box> : <></>}
      <Stack alignItems='center' direction='row' gap={1} height='100%' >
        {avatar ? <Box className='avatar' sx={{ width: avatarSize }}>{avatar}</Box> : <></>}
        <Box className='content'>
          <Stack direction='column'>
            {label
              ? <Typography
                data-testid={props['data-testid'] ? `${props['data-testid']}-label` : undefined}
                className='label'
                component='div'
                variant='dataCardLabel'
                style={{
                  fontSize: labelStyles.fontSize,
                  lineHeight: labelStyles.lineHeight,
                  wordBreak: labelStyles.wordBreak,
                  whiteSpace: labelStyles.whiteSpace,
                  overflow: labelStyles.overflow,
                  textOverflow: labelStyles.textOverflow,
                }}
              >
                {label}
              </Typography>
              : <></>
            }
            {title
              ? <Typography
                data-testid={props['data-testid'] ? `${props['data-testid']}-title` : undefined}
                className='title'
                component='div'
                variant='dataCardTitle'
                style={{
                  fontSize: titleStyles.fontSize,
                  lineHeight: titleStyles.lineHeight,
                  wordBreak: titleStyles.wordBreak,
                  whiteSpace: titleStyles.whiteSpace,
                  overflow: titleStyles.overflow,
                  textOverflow: titleStyles.textOverflow,
                }}
              >
                {href ? <Link component='a' href={href}>{title}</Link> : title}
              </Typography>
              : <></>
            }
            {hint
              ? <Typography
                data-testid={props['data-testid'] ? `${props['data-testid']}-hint` : undefined}
                className='hint'
                component='div'
                variant='dataCardHint'
                style={{
                  fontSize: hintStyles.fontSize,
                  lineHeight: hintStyles.lineHeight,
                  wordBreak: hintStyles.wordBreak,
                  whiteSpace: hintStyles.whiteSpace,
                  overflow: hintStyles.overflow,
                  textOverflow: hintStyles.textOverflow,
                }}
              >
                {hint}
              </Typography>
              : <></>
            }
          </Stack>
        </Box>
      </Stack>
    </Container>
  </Tooltip>;
};
